<template>
  <div class="my-auto text-base flex flex-col gap-5 text-center">
    <div class="flex flex-col gap-2">
      <h2 v-if="title" class="m-0 text-xl font-semibold text-foreground">
        {{ title }}
      </h2>
      <!-- Callers compose the content from translated strings with <br> breaks. -->
      <div class="text-sm text-foreground/70" v-html="content" />
    </div>

    <div class="flex flex-col gap-2">
      <ui-button
        :variant="destructive ? 'destructive' : 'default'"
        class="h-11 w-full"
        :disabled="progress"
        @click="() => actionConfirm()"
      >
        <loader-circle-icon v-if="progress" class="animate-spin" />
        <template v-else>
          {{ labelConfirm }}
        </template>
      </ui-button>

      <button
        type="button"
        data-slot="confirmation-cancel"
        class="mx-auto rounded-md px-3 py-1.5 text-sm text-foreground/60 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        @click="() => actionCancel()"
      >
        {{ labelCancel }}
      </button>
    </div>
  </div>
</template>

<script>
import { LoaderCircle } from '@lucide/vue';

/** Content of $confirmation.open(): rendered inside the $fullscreenModal frame. */
export default {
  name: 'ConfirmationDialog',
  components: {
    LoaderCircleIcon: LoaderCircle,
  },
  props: {
    title: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
    content: {
      type: String,
      required: true,
    },
    labelCancel: {
      type: String,
      required: false,
      default: 'Cancel',
    },
    labelConfirm: {
      type: String,
      required: false,
      default: 'OK',
    },
    // Red confirm button for irreversible actions (delete, reset).
    destructive: {
      type: Boolean,
      required: false,
      default: false,
    },
    confirm: {
      type: [ Function, null ],
      required: false,
      default: null,
    },
    cancel: {
      type: [ Function, null ],
      required: false,
      default: null,
    },
  },
  emits: [ 'close' ],
  data: () => ({
    progress: false,
  }),
  methods: {
    close() {
      this.$emit('close');

      if (typeof this.cancel === 'function') {
        this.cancel();
      }
    },
    actionCancel() {
      if (this.progress) {
        return;
      }

      this.close();
    },
    actionConfirm() {
      if (this.progress) {
        return;
      }

      if (typeof this.confirm !== 'function') {
        this.close();
        return;
      }

      const resp = this.confirm();
      if (resp instanceof Promise) {
        this.progress = true;
        resp
          .then(() => this.close())
          .catch(() => {
            // eslint-disable-next-line no-console
            console.error('ConfirmationModal: Promise rejected');
          })
          .finally(() => this.progress = false);
      }
      else {
        this.close();
      }
    },
  },
};
</script>
